import { Box, Flex, Image, useColorMode } from '@chakra-ui/react';

import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getBookListPagePath } from '../../App';
import Button from '../../components/button';
import Heading from '../../components/heading';
import LabeledSelect from '../../components/labeled-select';
import Layout from '../../components/layout';
import StepHeader, { FlowFooterNote } from '../../components/step-header';
import Text from '../../components/text';
import { useSession } from '../../hooks/useSession';
import SwalService from '../../services/swal';
import { ISelectOption } from '../../types';
import { IBookSelectOption } from './select-root';

export interface ISelectLocationsProps {
    selectedState: ISelectOption | null;
    selectedCountry: ISelectOption | null;
    setSelectedCountry: (countryOption: ISelectOption) => void;
    setSelectedState: (countryOption: ISelectOption) => void;
    countryOptions: ISelectOption[];
    getDisplayRegions: (selectedCountry: ISelectOption) => ISelectOption[];
    bookOptions: IBookSelectOption[];
}

export default function SelectLocations(props: ISelectLocationsProps) {
    const {
        selectedState,
        selectedCountry,
        setSelectedCountry,
        countryOptions,
        setSelectedState,
        getDisplayRegions,
        bookOptions,
    } = props;
    const session = useSession();
    const { push } = useHistory();
    const { colorMode, toggleColorMode } = useColorMode();
    const isLight = colorMode === 'light';

    useEffect(() => {
        if (
            session?.uiMode &&
            session?.uiMode != 'system' &&
            session?.uiMode != colorMode
        )
            toggleColorMode();
    }, []);

    //auto scroll to top of page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Layout
            colorMode={colorMode}
            alert={session?.user?.isSandbox ? 'SANDBOX MODE' : ''}
            stepHeader={<StepHeader step={2} label="Location" />}
            footerNote={<FlowFooterNote />}
            buttons={
                <Button
                    isDisabled={!selectedState || !selectedCountry}
                    onClick={() => {
                        if (bookOptions.length > 0) {
                            push(
                                getBookListPagePath(
                                    session?.cid!,
                                    selectedState?.value!
                                )
                            );
                        } else {
                            SwalService.noBookForLocationSwal();
                        }
                    }}
                    w="full"
                >
                    Continue
                </Button>
            }
            hasBackButton={session?.user?.backButton}
        >
            <Flex direction="column" align="flex-start" gap="24px" w="full">
                {session?.logoUrl && (
                    <Image
                        alignSelf="center"
                        maxH="48px"
                        maxW="160px"
                        objectFit="contain"
                        src={session?.logoUrl}
                        alt={session?.user?.name || ''}
                    />
                )}

                <Flex direction="column" align="center" gap="6px" w="full">
                    <Heading as="h1" fontSize="26px" textAlign="center">
                        Where do you use your account?
                    </Heading>
                    <Text size="sm" opacity={0.75} textAlign="center">
                        We'll show platforms legally available in your area.
                    </Text>
                </Flex>

                <Flex direction="column" gap="16px" w="full">
                    <LabeledSelect
                        label="Country"
                        value={selectedCountry}
                        onChange={(value: any) =>
                            setSelectedCountry(value as ISelectOption)
                        }
                        placeholder="Select your country"
                        options={countryOptions}
                        colorMode={session?.uiMode}
                        size="lg"
                    />
                    <LabeledSelect
                        label="State / Region"
                        placeholder="Select your state"
                        isDisabled={!selectedCountry}
                        value={selectedCountry && selectedState}
                        onChange={(value: any) =>
                            selectedCountry &&
                            setSelectedState(value as ISelectOption)
                        }
                        options={
                            (selectedCountry &&
                                getDisplayRegions(selectedCountry)) ||
                            []
                        }
                        colorMode={session?.uiMode}
                        size="lg"
                    />
                </Flex>

                <Box
                    w="full"
                    bg={isLight ? 'skySoft' : 'alphaWhite005'}
                    borderRadius={12}
                    px="16px"
                    py="14px"
                    mb="40px"
                >
                    <Text
                        size="xs"
                        opacity={0.7}
                        textAlign="left"
                        lineHeight="18px"
                    >
                        Availability varies by jurisdiction. Your location is
                        only used for filtering.
                    </Text>
                </Box>
            </Flex>
        </Layout>
    );
}
